import { useEffect, useMemo, useRef, useState } from "react";
import { createDefaultDrawingDocument } from "../schema/drawingSheet.schema";
import {
  alignViewportToGrid,
  clampViewportToSheet,
  moveViewport,
  resizeViewport,
} from "../engine/sheetLayoutUtils";

export default function useDrawingSheetState(initialDocument = null, onChange = null) {
  const [document, setDocument] = useState(
    () => initialDocument || createDefaultDrawingDocument()
  );

  const lastInitialRef = useRef(null);
  const lastEmittedRef = useRef(null);

  useEffect(() => {
    if (!initialDocument) return;

    const serialized = JSON.stringify(initialDocument);

    if (lastInitialRef.current === serialized) return;

    lastInitialRef.current = serialized;
    lastEmittedRef.current = serialized;

    setDocument(initialDocument);
  }, [initialDocument]);

  const activeSheet = useMemo(() => {
    return (
      document.sheets.find((sheet) => sheet.id === document.activeSheetId) ||
      document.sheets[0] ||
      null
    );
  }, [document]);

  function emitDocumentChange() {
  if (!onChange || !document) return;

  lastEmittedRef.current = JSON.stringify(document);
  onChange(document);
}

  function updateDocument(updater) {
  setDocument((prev) => {
    const next = typeof updater === "function" ? updater(prev) : updater;

    return {
      ...next,
      updatedAt: new Date().toISOString(),
    };
  });
}

  function replaceViewportAnnotations(sheetId, viewportId, nextAnnotations = []) {
  updateSheet(sheetId, (sheet) => ({
    ...sheet,
    annotations: [
      ...(sheet.annotations || []).filter(
        (ann) => ann.viewportId !== viewportId
      ),
      ...nextAnnotations,
    ],
  }));
}

  function updateSheet(sheetId, patch) {
    updateDocument((prev) => ({
      ...prev,
      sheets: prev.sheets.map((sheet) =>
        sheet.id === sheetId
          ? {
              ...sheet,
              ...(typeof patch === "function" ? patch(sheet) : patch),
            }
          : sheet
      ),
    }));
  }

  function updateView(viewId, patch) {
    updateDocument((prev) => ({
      ...prev,
      views: prev.views.map((view) =>
        view.id === viewId
          ? {
              ...view,
              ...(typeof patch === "function" ? patch(view) : patch),
              updatedAt: new Date().toISOString(),
            }
          : view
      ),
    }));
  }

  function updateViewport(sheetId, viewportId, patch) {
    updateSheet(sheetId, (sheet) => ({
      ...sheet,
      viewports: sheet.viewports.map((vp) =>
        vp.id === viewportId
          ? {
              ...vp,
              ...(typeof patch === "function" ? patch(vp, sheet) : patch),
            }
          : vp
      ),
    }));
  }

  function moveSheetViewport(sheetId, viewportId, dx, dy, options = {}) {
    const snap = options.snap !== false;
    const grid = Number(options.grid || 10);

    updateViewport(sheetId, viewportId, (viewport, sheet) => {
      let next = moveViewport(viewport, dx, dy);

      if (snap) {
        next = alignViewportToGrid(next, grid);
      }

      next = clampViewportToSheet(next, sheet, 10);

      return next;
    });
  }

  function resizeSheetViewport(sheetId, viewportId, width, height, options = {}) {
    const snap = options.snap !== false;
    const grid = Number(options.grid || 10);

    updateViewport(sheetId, viewportId, (viewport, sheet) => {
      let next = resizeViewport(viewport, width, height);

      if (snap) {
        next = alignViewportToGrid(next, grid);
      }

      next = clampViewportToSheet(next, sheet, 10);

      return next;
    });
  }

  function addAnnotation(sheetId, annotation) {
    updateSheet(sheetId, (sheet) => ({
      ...sheet,
      annotations: [
        ...(sheet.annotations || []),
        {
          id: annotation.id || `ann_${Date.now()}`,
          ...annotation,
        },
      ],
    }));
  }

  function updateAnnotation(sheetId, annotationId, patch) {
    updateSheet(sheetId, (sheet) => ({
      ...sheet,
      annotations: (sheet.annotations || []).map((ann) =>
        ann.id === annotationId
          ? {
              ...ann,
              ...(typeof patch === "function" ? patch(ann) : patch),
            }
          : ann
      ),
    }));
  }

  function getViewById(viewId) {
    return document.views.find((view) => view.id === viewId) || null;
  }

  function deleteViewAndViewports(viewId) {
  if (!viewId) return;

  updateDocument((prev) => {
    const nextSheets = (prev.sheets || []).map((sheet) => ({
      ...sheet,
      viewports: (sheet.viewports || []).filter(
        (viewport) => String(viewport.viewId) !== String(viewId)
      ),
      annotations: (sheet.annotations || []).filter(
        (ann) => String(ann.viewId) !== String(viewId)
      ),
    }));

    return {
      ...prev,
      views: (prev.views || []).filter(
        (view) => String(view.id) !== String(viewId)
      ),
      sheets: nextSheets,
    };
  });
}

function deleteAnnotation(sheetId, annotationId) {
  updateSheet(sheetId, (sheet) => ({
    ...sheet,
    annotations: (sheet.annotations || []).filter(
      (ann) => String(ann.id) !== String(annotationId)
    ),
  }));
}

  return {
    document,
    setDocument,
    updateDocument,
    activeSheet,
    updateSheet,
    updateViewport,
    updateView,
    moveSheetViewport,
    resizeSheetViewport,
    addAnnotation,
    updateAnnotation,
    getViewById,
    replaceViewportAnnotations,
    deleteViewAndViewports,
    deleteAnnotation,
    emitDocumentChange,
  };
}